import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { aplanarMenu, getMenu } from '../navegacion/menuConfig';
import { getHomePorRol, getRolActual } from '../utils/rolUtils';

// Migas derivadas del menú: grupos (texto) + página actual.
export const migasDesdeMenu = (rol, ruta) => {
  const item = aplanarMenu(getMenu(rol)).find((i) => i.ruta === ruta);
  if (!item) return [];
  return [...item.grupos.map((g) => ({ label: g.label })), { label: item.label }];
};

export const Migas = ({ migas, sx }) => {
  const home = getHomePorRol(getRolActual());
  return (
    <Breadcrumbs
      aria-label="Ruta de navegación"
      separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
      sx={{ mb: 0.75, fontSize: '0.8rem', ...sx }}
    >
      <Link component={RouterLink} to={home} color="text.secondary" aria-label="Inicio" sx={{ display: 'flex', alignItems: 'center' }}>
        <HomeOutlinedIcon sx={{ fontSize: 17 }} />
      </Link>
      {migas.map((m, i) => {
        const esUltima = i === migas.length - 1;
        return m.to && !esUltima ? (
          <Link key={m.label} component={RouterLink} to={m.to} color="text.secondary">
            {m.label}
          </Link>
        ) : (
          <Typography
            key={m.label}
            sx={{ fontSize: 'inherit', fontWeight: esUltima ? 600 : 400 }}
            color={esUltima ? 'text.primary' : 'text.secondary'}
            aria-current={esUltima ? 'page' : undefined}
          >
            {m.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};

// Las páginas con PageHeader dibujan sus propias migas; las demás reciben las automáticas.
const MigasContext = createContext({ registrar: () => () => {}, hayPropias: false });

export const MigasProvider = ({ children }) => {
  const [propias, setPropias] = useState(0);
  const registrar = useCallback(() => {
    setPropias((n) => n + 1);
    return () => setPropias((n) => n - 1);
  }, []);
  const value = useMemo(() => ({ registrar, hayPropias: propias > 0 }), [registrar, propias]);
  return <MigasContext.Provider value={value}>{children}</MigasContext.Provider>;
};

// Antes del pintado, para que no parpadeen las migas automáticas
export const useMigasPropias = () => {
  const { registrar } = useContext(MigasContext);
  useLayoutEffect(() => registrar(), [registrar]);
};

export const MigasAutomaticas = () => {
  const { hayPropias } = useContext(MigasContext);
  const { pathname } = useLocation();
  const rol = getRolActual();
  const migas = useMemo(() => migasDesdeMenu(rol, pathname), [rol, pathname]);

  if (hayPropias || migas.length === 0 || pathname === getHomePorRol(rol)) return null;
  return <Migas migas={migas} sx={{ mb: 1 }} />;
};
